import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, ShoppingBag, Truck, ShieldCheck, Check, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { CartDrawer } from "./CartDrawer";
import { Button } from "./ui/button";
import {
  getProduct,
  products as ALL,
  SIZES,
  FRAMES,
  formatCOP,
  computeVariantPrice,
} from "../data/products";
import { useCart } from "../context/CartContext";
import { ProductCard } from "./ProductCard";

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = useMemo(() => getProduct(id), [id]);

  const { addItem, openCart } = useCart();
  const [sizeId, setSizeId] = useState(product?.defaultSize || "m");
  const [frameId, setFrameId] = useState("negro");

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
        <Navbar />
        <main className="pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-serif text-4xl text-slate-900 mb-4">
            Esta pieza no existe
          </h1>
          <p className="text-slate-600 mb-8">
            Es posible que el cuadro haya salido de catálogo.
          </p>
          <Button
            onClick={() => navigate("/")}
            data-testid="pdp-back-home"
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6"
          >
            Volver al inicio
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const unitPrice = computeVariantPrice(null, sizeId);
  const selectedSize = SIZES.find((s) => s.id === sizeId);
  const selectedFrame = FRAMES.find((f) => f.id === frameId);

  const related = ALL.filter(
    (p) => p.id !== product.id && p.category === product.category
  ).slice(0, 4);

  const handleAdd = () => {
    addItem({
      productId: product.id,
      title: product.title,
      image: product.image,
      sizeId,
      sizeLabel: selectedSize.label,
      frameId,
      frameLabel: selectedFrame.label,
      unitPrice,
    });
    toast.success("Cuadro añadido a tu carrito", {
      description: `${product.title} · ${selectedSize.label} · ${selectedFrame.label}`,
    });
  };

  const handleBuyNow = () => {
    handleAdd();
    setTimeout(() => openCart(), 250);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      <Navbar />
      <CartDrawer />

      <main className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/categoria/${product.categorySlug}`}
            data-testid="pdp-back-link"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            Volver a {product.category}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Image */}
            <div
              data-testid="pdp-image-container"
              className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-200 shadow-md"
            >
              <img
                src={product.image}
                alt={product.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <span className="absolute top-4 left-4 inline-flex items-center bg-white/85 backdrop-blur-sm text-slate-800 text-[11px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full">
                {product.category}
              </span>
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <Link
                to={`/categoria/${product.categorySlug}`}
                data-testid="pdp-category-link"
                className="text-xs uppercase tracking-[0.22em] text-[#E85A3C] hover:underline mb-3 w-fit"
              >
                {product.category}
              </Link>
              <h1
                data-testid="pdp-title"
                className="font-serif text-4xl md:text-5xl text-slate-900 leading-[1.05] tracking-tight"
              >
                {product.title}
              </h1>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {product.description}
              </p>

              <div className="mt-6 flex items-baseline gap-3">
                <span
                  data-testid="pdp-price"
                  className="font-serif text-3xl text-slate-900 tabular-nums"
                >
                  {formatCOP(unitPrice)}
                </span>
              </div>
              <div
                data-testid="pdp-shipping-nudge"
                className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-500"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5 text-[#E85A3C]" strokeWidth={1.75} />
                  Envío gratis desde {formatCOP(200000)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-[#E85A3C]" strokeWidth={1.75} />
                  Listo en 5–7 días hábiles
                </span>
              </div>

              {/* Size selector */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-900 uppercase tracking-wider">
                    Tamaño
                  </h3>
                  <span className="text-xs text-slate-500">
                    {selectedSize.label}
                  </span>
                </div>
                <div
                  data-testid="pdp-size-selector"
                  className="grid grid-cols-4 gap-2"
                >
                  {SIZES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSizeId(s.id)}
                      data-testid={`pdp-size-${s.id}`}
                      className={`group relative px-3 py-3 rounded-xl border text-sm transition-all duration-200 ${
                        sizeId === s.id
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                      }`}
                    >
                      <span className="block font-medium">{s.label}</span>
                      <span
                        className={`block text-[10px] mt-0.5 ${
                          sizeId === s.id ? "text-slate-300" : "text-slate-400"
                        }`}
                      >
                        {formatCOP(s.price)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frame selector */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-900 uppercase tracking-wider">
                    Marco
                  </h3>
                  <span className="text-xs text-slate-500">
                    {selectedFrame.label}
                  </span>
                </div>
                <div
                  data-testid="pdp-frame-selector"
                  className="flex flex-wrap gap-3"
                >
                  {FRAMES.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setFrameId(f.id)}
                      data-testid={`pdp-frame-${f.id}`}
                      aria-label={f.label}
                      className={`relative h-12 w-12 rounded-full border-2 transition-all duration-200 ${
                        frameId === f.id
                          ? "border-[#E85A3C] ring-2 ring-[#E85A3C]/30"
                          : "border-slate-200 hover:border-slate-400"
                      }`}
                      style={{
                        background:
                          f.hex === "transparent"
                            ? "repeating-linear-gradient(45deg, #f1f5f9, #f1f5f9 4px, #ffffff 4px, #ffffff 8px)"
                            : f.hex,
                      }}
                    >
                      {frameId === f.id && (
                        <Check
                          className={`absolute inset-0 m-auto h-4 w-4 ${
                            f.id === "negro" || f.id === "madera"
                              ? "text-white"
                              : "text-slate-900"
                          }`}
                          strokeWidth={3}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* CTAs */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={handleAdd}
                  data-testid="pdp-add-to-cart"
                  variant="outline"
                  className="h-12 rounded-full border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Añadir al carrito
                </Button>
                <Button
                  onClick={handleBuyNow}
                  data-testid="pdp-buy-now"
                  className="h-12 rounded-full bg-[#E85A3C] hover:bg-[#D3492D] text-white shadow-lg shadow-[#E85A3C]/30"
                >
                  Comprar ahora
                </Button>
              </div>

              {/* Trust badges */}
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <Truck className="h-4 w-4 mt-0.5 text-[#E85A3C]" strokeWidth={1.75} />
                  Envío 100% garantizado a todo el país.
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 text-[#E85A3C]" strokeWidth={1.75} />
                  Impreso sobre MDF de excelente calidad.
                </li>
                <li className="flex items-start gap-2 sm:col-span-2">
                  <CreditCard className="h-4 w-4 mt-0.5 text-[#E85A3C]" strokeWidth={1.75} />
                  Paga fácil con tarjeta, PSE o{" "}
                  <span className="font-medium text-slate-900">Sistecrédito</span>.
                </li>
              </ul>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-20 md:mt-28">
              <h2 className="font-serif text-2xl md:text-3xl text-slate-900 mb-8">
                También te puede gustar
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {related.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
